<template>
  <a
    :href="watchUrl"
    target="_blank"
    rel="noopener noreferrer"
    class="wa-yt-preview"
    @click.stop
  >
    <div class="wa-yt-preview__thumb-wrap">
      <img
        :src="thumbnailUrl"
        alt=""
        class="wa-yt-preview__thumb"
        loading="lazy"
        @error="onThumbError"
      />
      <div class="wa-yt-preview__play" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
      </div>
    </div>
    <div class="wa-yt-preview__meta">
      <p class="wa-yt-preview__title">{{ title || t('integracao.watchYoutube') }}</p>
      <p class="wa-yt-preview__domain">{{ t('integracao.youtubeDomain') }}</p>
    </div>
  </a>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { youtubeThumbnailUrl, normalizeYoutubeWatchUrl } from '../utils/youtubeLink'

const { t } = useI18n()

const props = defineProps({
  videoId: { type: String, required: true },
  url: { type: String, default: '' }
})

const title = ref('')
const thumbQuality = ref('hqdefault')

const watchUrl = computed(() =>
  props.url || normalizeYoutubeWatchUrl(props.videoId)
)

const thumbnailUrl = computed(() =>
  youtubeThumbnailUrl(props.videoId, thumbQuality.value)
)

const onThumbError = () => {
  if (thumbQuality.value !== 'mqdefault') {
    thumbQuality.value = 'mqdefault'
  }
}

onMounted(async () => {
  try {
    const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl.value)}&format=json`
    const res = await fetch(oembedUrl)
    if (!res.ok) return
    const data = await res.json()
    if (data?.title) title.value = data.title
  } catch {
    /* título opcional */
  }
})
</script>

<style scoped>
.wa-yt-preview {
  display: block;
  margin: 4px 4px 6px;
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: opacity 0.15s ease;
}

.wa-yt-preview:hover {
  opacity: 0.92;
}

:global(body.tema_escura) .wa-yt-preview,
:global(.dark) .wa-yt-preview {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.08);
}

.wa-yt-preview__thumb-wrap {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #000;
}

.wa-yt-preview__thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.wa-yt-preview__play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
}

.wa-yt-preview__play svg {
  width: 48px;
  height: 48px;
  color: #fff;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
}

.wa-yt-preview__meta {
  padding: 8px 10px 10px;
}

.wa-yt-preview__title {
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #111b21;
}

.wa-yt-preview__domain {
  font-size: 0.75rem;
  margin: 4px 0 0;
  color: #667781;
}

:global(body.tema_escura) .wa-yt-preview__title,
:global(.dark) .wa-yt-preview__title {
  color: #e9edef;
}

:global(body.tema_escura) .wa-yt-preview__domain,
:global(.dark) .wa-yt-preview__domain {
  color: #8696a0;
}
</style>
